<template>
  <div id="events-section" v-if="hasVisibleContent">
    <!-- Toggle -->
    <div class="events-toggle d-f jc-c p-t-lg p-b">
      <span
        class="tt-up pointer"
        :class="activeFilter === 'upcoming' ? '' : 'mute'"
        @click="setFilter('upcoming')"
      >Upcoming Events</span>
      <span class="mute m-l m-r">/</span>
      <span
        class="tt-up pointer"
        :class="activeFilter === 'previous' ? '' : 'mute'"
        @click="setFilter('previous')"
      >Previous Events</span>
    </div>

    <div v-if="!loading && filteredEvents.length === 0" class="ta-c mute p">
      No {{ activeFilter }} events
    </div>

    <!-- 2-col desktop / 1-col mobile grid -->
    <div class="events-grid" :class="{ 'single-event': pagedEvents.length === 1 }" v-if="pagedEvents.length">
      <div
        v-for="event in pagedEvents"
        :key="event.id"
        class="event-row"
      >
        <div class="d-f ai-c g-half p-t-sm p-b-sm">
          <span
            class="fi fis event-flag"
            :class="event.countryCode ? `fi-${event.countryCode}` : ''"
          ></span>
          <span class="mute">{{ event.city }}<span v-if="event.venue">, {{ event.venue }}</span></span>
        </div>

        <h2 class="event-date tt-up mute m-0 m-t-sm m-b-half" v-html="formatDate(event.date)"></h2>
        <h2 class="event-name m-0 m-b-half">{{ event.name }}</h2>

        <!-- Description / Tickets row -->
        <div class="event-bottom p-b-sm p-t-sm bs-bb b-bottom" v-if="event.description || event.ticketUrl">

          <div
            class="d-f ai-fs g-half"
            :class="{ pointer: overflowMap[event.id] }"
            @click="overflowMap[event.id] && toggleExpand(event.id)"
          >
            <IconBase v-if="overflowMap[event.id]" class="arrow" :class="{ expanded: expandedMap[event.id] }" :w="15" :h="15">
              <IconChevronRight />
            </IconBase>

            <div class="desc-content flex-1">
              <!-- Collapsed: v-show keeps ref in DOM for overflow measurement -->
              <div class="d-f ai-c g-half collapsed-row" v-show="!overflowMap[event.id] || !expandedMap[event.id]">
                <span
                  v-if="event.description"
                  :ref="`desc-${event.id}`"
                  class="mute desc-inline flex-1"
                >{{ event.description }}</span>
                <a
                  v-if="event.ticketUrl"
                  :href="normalizeUrl(event.ticketUrl)"
                  target="_blank"
                  rel="noopener"
                  class="tickets-link tt-up text-link ws-nowrap fs-0"
                  :class="{ 'm-l-sm': event.description }"
                  @click.stop
                >Tickets</a>
              </div>

              <!-- Expanded -->
              <div v-if="overflowMap[event.id] && expandedMap[event.id]" class="d-f fd-c g-half">
                <span class="mute">{{ event.description }}</span>
                <div class="d-f jc-fe" v-if="event.ticketUrl">
                  <a
                    :href="normalizeUrl(event.ticketUrl)"
                    target="_blank"
                    rel="noopener"
                    class="tickets-link tt-up text-link ws-nowrap"
                    @click.stop
                  >Tickets</a>
                </div>
              </div>
            </div>
          </div>

        </div>

      </div>
    </div>

    <!-- Pagination dots -->
    <div class="d-f jc-c g p-t p-b" v-if="totalPages > 1">
      <span
        v-for="p in totalPages"
        :key="p"
        class="pagination-dot pointer"
        :class="{ active: p - 1 === currentPage }"
        @click="currentPage = p - 1"
      ></span>
    </div>
  </div>
</template>

<script>
import { rtdb } from '@/db.js';
import { isMobileMixin } from '@/mixins/isMobileMixin';
import IconBase from '@/assets/icons/IconBase.vue';
import IconChevronRight from '@/assets/icons/IconChevronRight.vue';

export default {
  name: 'EventsSection',
  mixins: [isMobileMixin],
  components: { IconBase, IconChevronRight },
  props: {
    userId: { type: String, required: true },
  },
  data() {
    return {
      events: [],
      loading: true,
      activeFilter: 'upcoming',
      currentPage: 0,
      overflowMap: {},
      expandedMap: {},
      _eventsRef: null,
    };
  },
  mounted() {
    this.fetchEvents();
  },
  beforeDestroy() {
    if (this._eventsRef) this._eventsRef.off();
  },
  watch: {
    userId(newVal) {
      if (!newVal) return;
      if (this._eventsRef) this._eventsRef.off();
      this.fetchEvents();
    },
    activeFilter() {
      this.currentPage = 0;
    },
    pagedEvents() {
      this.$nextTick(() => this.$nextTick(() => this.measureOverflow()));
    },
  },
  computed: {
    publishedEvents() {
      return this.events.filter(e => e.published);
    },
    filteredEvents() {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      return this.publishedEvents
        .filter(e => {
          const d = new Date(e.date);
          return this.activeFilter === 'upcoming' ? d >= now : d < now;
        })
        .sort((a, b) => {
          const da = new Date(a.date);
          const db = new Date(b.date);
          return this.activeFilter === 'upcoming' ? da - db : db - da;
        });
    },
    pageSize() {
      return this.isMobile() ? 3 : 6;
    },
    totalPages() {
      return Math.ceil(this.filteredEvents.length / this.pageSize);
    },
    pagedEvents() {
      const start = this.currentPage * this.pageSize;
      return this.filteredEvents.slice(start, start + this.pageSize);
    },
    hasVisibleContent() {
      return !this.loading && this.publishedEvents.length > 0;
    },
  },
  methods: {
    fetchEvents() {
      this.loading = true;
      this._eventsRef = rtdb.ref(`users/${this.userId}/events`);
      this._eventsRef.on('value', snapshot => {
        const val = snapshot.val() || {};
        this.events = Object.entries(val).map(([id, data]) => ({ id, ...data }));
        this.loading = false;
        // Double nextTick: first tick renders DOM, second lets browser finish layout
        this.$nextTick(() => this.$nextTick(() => this.measureOverflow()));
      });
    },
    measureOverflow() {
      this.pagedEvents.forEach(event => {
        if (!event.description) return;
        const ref = this.$refs[`desc-${event.id}`];
        const el = Array.isArray(ref) ? ref[0] : ref;
        if (!el || el.clientWidth === 0) return;

        // Clone to measure unconstrained natural text width — avoids unreliable scrollWidth
        // on elements where overflow:hidden or flex may affect the reported value
        const clone = el.cloneNode(true);
        clone.style.cssText += ';position:absolute;top:-9999px;left:-9999px;visibility:hidden;white-space:nowrap;width:auto;max-width:none;overflow:visible;flex:none;';
        document.body.appendChild(clone);
        const naturalWidth = clone.offsetWidth;
        document.body.removeChild(clone);

        this.$set(this.overflowMap, event.id, naturalWidth > el.clientWidth);
      });
    },
    toggleExpand(id) {
      this.$set(this.expandedMap, id, !this.expandedMap[id]);
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr + 'T00:00:00');
      const day = d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
      const num = String(d.getDate()).padStart(2, '0');
      const month = d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
      const year = d.getFullYear();
      const s = '<span class="date-slash">/</span>';
      return `${day}${s}${num} ${month}${s}${year}`;
    },
    normalizeUrl(url) {
      if (!url) return '#';
      return /^https?:\/\//i.test(url.trim()) ? url.trim() : `https://${url.trim()}`;
    },
    setFilter(filter) {
      this.activeFilter = filter;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/scss/_variables.scss";
@import "@/scss/_classes.scss";
@import "@/scss/_mixins.scss";

#events-section {
  width: 100%;
  box-sizing: border-box;

  .events-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: $mg-desktop-xl;

    &.single-event {
      grid-template-columns: 1fr;
      max-width: 50%;
      margin: 0 auto;

      @media #{$mobile} {
        max-width: 100%;
      }
    }

    @media #{$mobile} {
      grid-template-columns: 1fr;
    }

    .event-row {
      min-width: 0; // grid items default to min-width:auto — must override so cells can shrink
      box-sizing: border-box;

      .event-flag {
        width: 15px;
        height: 15px;
        border-radius: 50% !important;
        flex-shrink: 0;
        background-size: cover;
      }

      .event-name, .event-date {
        font-weight: $reg;
        line-height: 1.1;
        @media #{$mobile} {
          font-size: $h2-d;
        }
      }

      .event-date {
        ::v-deep .date-slash {
          opacity: 0.3;
        }
      }

      .event-bottom {
        width: 100%;
        &:hover {
          border-bottom-color: $white;
        }
      }

      .desc-content {
        min-width: 0;
        overflow: hidden;
      }

      .collapsed-row {
        min-width: 0;
        overflow: hidden; // constrains the row so desc-inline clientWidth is bounded

        .desc-inline {
          display: block; // span needs block formatting for overflow:hidden to clip text
          min-width: 0;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }
      }

      .tickets-link {
        flex-shrink: 0;
      }

      .arrow {
        margin-top: 3px;
        flex-shrink: 0;
        transition: transform 0.2s ease;
        &.expanded {
          transform: rotate(90deg);
        }
        @media #{$mobile} {
          margin-top: 1px;
        }
      }
    }
  }

  .pagination-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: $line-color;
    display: inline-block;
    transition: background-color 0.2s;

    &.active {
      background-color: $white;
    }
  }
}
</style>
